import React, { useEffect, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormSelect,
  CFormInput,
  CModal,
  CForm,
  CFormLabel,
  CFormTextarea,
} from "@coreui/react";
import AppToast from "../../components/seller/AppToast";
import AppLoader from "../../components/seller/AppLoader";
import { useDispatch, useSelector } from "react-redux";
import { capitalizeFirst } from "../../utils/stringUtils";
import { timeAgo } from "../../utils/timeAgo";
import {
  getReceivedOffers,
  respondCounterOffer,
} from "../../features/seller/sellerSlice";
import AppButton from "../../components/buyer/AppButton";
import { formatCurrency, formatPrice } from "../../utils/price";

import { useNavigate } from "react-router-dom";

const ReceivedOffers = () => {
  const dispatch = useDispatch();
  const {
    data: offers,
    loading,
    error,
  } = useSelector((state) => state.seller.receivedOffers);

  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");
  const [actionModal, setActionModal] = useState({
    open: false,
    type: null, // 'accept' | 'reject' | 'counter'
    offer: null,
  });

  const [note, setNote] = useState("");
  const [counterPrice, setCounterPrice] = useState("");
  const [counterDiscount, setCounterDiscount] = useState("");
  const [counterCurrency, setCounterCurrency] = useState("");
  const [errors, setErrors] = useState({});

  const { loading: respondLoading, error: respondError } = useSelector(
    (state) => state.seller.respondCounterOffer
  );

  console.log("Response:", offers);

  useEffect(() => {
    dispatch(getReceivedOffers())
      .unwrap()
      .catch((err) => {
        // Handle error if needed
        console.log("Failed to fetch received offers", err);
      });
  }, [dispatch]);

  const filtered = offers.filter((r) =>
    (r.scrap_type || "").toLowerCase().includes(search.toLowerCase())
  );

  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = filtered.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filtered.length / entriesPerPage);

  const fields = [
    "#",
    "Offer ID",
    "Listing ID",
    "Image",
    "Name",
    "Category",
    "Subcategory",
    "Quantity",
    "Unit",
    "Max Buying Price (AUD)",
    "Discount %",
    "Discounted Price (AUD)",
    "Location",
    "Status",
    "Offer Status",
    "Notes",
    "Actions",
  ];

  const validate = () => {
    const newErrors = {};

    if (actionModal.type !== "accept" && !note.trim()) {
      newErrors.note = "Note is required";
    }

    if (actionModal.type === "counter" && !counterCurrency) {
      newErrors.counterCurrency = "Currency is required";
    }

    if (actionModal.type === "counter") {
      const maxBuyingPrice = Number(actionModal.offer?.max_buying_price);
      const enteredPrice = Number(counterPrice);
      const enteredDiscount = Number(counterDiscount);

      if (!counterPrice) {
        newErrors.counterPrice = "Counter price is required";
      } else if (enteredPrice <= maxBuyingPrice) {
        newErrors.counterPrice = `Counter price must be greater than ${maxBuyingPrice}`;
      }

      if (!counterDiscount) {
        newErrors.counterDiscount = "Discount % is required";
      } else if (enteredDiscount <= 0 || enteredDiscount > 100) {
        newErrors.counterDiscount = "Discount must be between 1 and 100";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const actionMap = {
    accept: "accepted",
    reject: "rejected",
    counter: "counter",
  };

  const handleSubmitAction = async () => {
    if (!validate()) return;

    const apiAction = actionMap[actionModal.type];

    const data = {
      action: apiAction,
      note: note || null,
    };

    if (actionModal.type === "counter") {
      data.price = Number(counterPrice);
      data.discount_percentage = Number(counterDiscount);
      data.currency = counterCurrency;
    }

    console.log("Action:", actionModal.type, data, actionModal.offer.id);

    try {
      await dispatch(
        respondCounterOffer({
          id: actionModal.offer.id,
          data,
        })
      ).unwrap();

      setToastType("success");
      setToastMessage(
        actionModal.type === "accept"
          ? "Offer accepted successfully"
          : actionModal.type === "reject"
            ? "Offer rejected successfully"
            : "Counter offer sent successfully"
      );
      setToastVisible(true);

      resetModal();
      dispatch(getReceivedOffers()); // refresh table
    } catch (err) {
      setToastType("danger");
      setToastMessage(err || "Something went wrong");
      setToastVisible(true);
    }
  };

  const resetModal = () => {
    setActionModal({ open: false, type: null, offer: null });
    setNote("");
    setCounterPrice("");
    setCounterDiscount("");
    setCounterCurrency("");
    setErrors({});
  };

  const OFFER_STATUS_CONFIG = {
    pending: {
      label: "Pending",
      color: "secondary",
      canTakeAction: true,
    },
    seller_countered: {
      label: "Countered",
      color: "warning",
      canTakeAction: false,
    },
    accepted: {
      label: "Accepted",
      color: "success",
      canTakeAction: false,
    },
    rejected: {
      label: "Rejected",
      color: "danger",
      canTakeAction: false,
    },
  };


  // Navigation hook for row click offer detail
  const navigate = useNavigate();

  const handleRowClick = (offertId) => {
    navigate(`/seller/offers-detail/${offertId}`);
  };


  if (loading) {
    return <AppLoader />;
  }
  return (
    <>
      <div className="container-fluid px-3">
        <CRow className="mt-4 mb-3">
          <CCol>
            <h4 className="mb-0">Received Offers</h4>
          </CCol>
        </CRow>
      </div>
      <CCard className="mb-4">
        <CCardHeader>
          <CRow className="mb-2">
            <CCol>
              <h6
                className="text-muted mb-0 d-flex align-items-center"
                style={{ height: "100%" }}
              >
                Offers placed by buyers
              </h6>
            </CCol>
          </CRow>

          <hr className="my-2" />
          <CRow className="align-items-center">
            <CCol md={6}>
              Show{" "}
              <CFormSelect
                size="sm"
                value={entriesPerPage}
                onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                style={{ display: "inline-block", width: "auto" }}
                options={[
                  { label: "5", value: "5" },
                  { label: "10", value: "10" },
                  { label: "25", value: "25" },
                ]}
              />{" "}
              entries
            </CCol>
            <CCol md={6} className="text-end">
              Search:{" "}
              <CFormInput
                type="text"
                size="sm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ display: "inline-block", width: "200px" }}
              />
            </CCol>
          </CRow>
        </CCardHeader>
        <CCardBody>
          <CTable hover responsive bordered>
            <CTableHead color="light">
              <CTableRow>
                {fields.map((h) => (
                  <CTableDataCell
                    key={h}
                    align="center"
                    sx={{ fontWeight: 600 }}
                  >
                    {h}
                  </CTableDataCell>
                ))}
              </CTableRow>
            </CTableHead>
            <CTableBody>
              {offers.map((offer, index) => {
                const hasOffers =
                  Array.isArray(offer.offers) && offer.offers.length > 0;
                const latestOffer = hasOffers
                  ? offer.offers[offer.offers.length - 1]
                  : null;
                const offerStatus = latestOffer?.status ?? null;

                const statusConfig = offerStatus
                  ? OFFER_STATUS_CONFIG[offerStatus] ||
                  OFFER_STATUS_CONFIG.pending
                  : null;

                const canTakeAction =
                  !offerStatus || statusConfig?.canTakeAction;

                return (
                  <CTableRow
                    key={offer.id}
                    sx={{
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.02)",
                      },
                    }} style={{ cursor: "pointer" }} onClick={() => handleRowClick(offer.id)}
                  >
                    <CTableDataCell align="center">{index + 1}</CTableDataCell>
                    <CTableDataCell align="center">
                      {offer.unique_offer_id || "-"}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {offer.listing_id}
                    </CTableDataCell>

                    <CTableDataCell align="center">
                      {offer.image_url ? (
                        <img
                          src={offer.image_url}
                          alt={offer.name}
                          loading="lazy"
                          className="img-fluid"
                          style={{
                            maxWidth: 80,
                            height: "auto",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </CTableDataCell>

                    <CTableDataCell align="center">
                      {offer.name || "-"}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {offer.category?.name || "-"}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {offer.subcategory?.name || "-"}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {offer.quantity || "-"}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {offer.unit_of_measurement || "-"}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {formatCurrency(offer.max_buying_price)}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {offer.discount_percentage || "-"}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {formatCurrency(offer.discounted_price)}
                    </CTableDataCell>

                    <CTableDataCell align="center">
                      {offer.location || "-"}
                    </CTableDataCell>

                    <CTableDataCell align="center">
                      {offer.status ? (
                        <span className="badge bg-success">
                          {capitalizeFirst(offer.status)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {statusConfig ? (
                        <span className={`badge bg-${statusConfig.color}`}>
                          {statusConfig.label}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </CTableDataCell>
                    {/* <CTableDataCell align="center">
                      {offerStatus ? (
                        <span
                          className={`badge bg-${
                            offerStatus === "accepted"
                              ? "success"
                              : offerStatus === "rejected"
                              ? "danger"
                              : offerStatus === "counter"
                              ? "warning"
                              : "secondary"
                          }`}
                        >
                          {capitalizeFirst(offerStatus)}
                        </span>
                      ) : (
                        "-"
                      )}
                    </CTableDataCell> */}

                    <CTableDataCell align="center">
                      {latestOffer?.note ?? "-"}
                    </CTableDataCell>
                    <CTableDataCell align="center">
                      {canTakeAction ? (
                        <>
                          <CButton
                            size="sm"
                            color="success"
                            className="me-1"
                            onClick={(e) =>{
                              e.stopPropagation();
                              setActionModal({
                                open: true,
                                type: "accept",
                                offer,
                              })}
                            }
                          >
                            Accept
                          </CButton>

                          <CButton
                            size="sm"
                            color="danger"
                            className="me-1"
                            onClick={(e) =>{
                              e.stopPropagation();
                              setActionModal({
                                open: true,
                                type: "reject",
                                offer,
                              })}
                            }
                          >
                            Reject
                          </CButton>

                          <CButton
                            size="sm"
                            color="warning"
                            className="text-white"
                            onClick={(e) =>{
                              e.stopPropagation();
                              setActionModal({
                                open: true,
                                type: "counter",
                                offer,
                              })}
                            }
                          >
                            Counter
                          </CButton>
                        </>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </CTableDataCell>
                  </CTableRow>
                );
              })}
            </CTableBody>
            {offers.length === 0 && (
              <CTableRow>
                <CTableDataCell colSpan="14" className="text-center text-muted">
                  No offers found.
                </CTableDataCell>
              </CTableRow>
            )}
          </CTable>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Showing {indexOfFirst + 1} to{" "}
              {Math.min(indexOfLast, filtered.length)} of {filtered.length}{" "}
              entries
            </div>
            <div className="d-flex align-items-center">
              <CButton
                size="sm"
                color="light"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                className="me-2"
              >
                Prev
              </CButton>

              {[...Array(totalPages)].map((_, i) => (
                <CButton
                  key={i}
                  size="sm"
                  color={currentPage === i + 1 ? "primary" : "light"}
                  onClick={() => setCurrentPage(i + 1)}
                  className="me-2"
                >
                  {i + 1}
                </CButton>
              ))}

              <CButton
                size="sm"
                color="light"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              >
                Next
              </CButton>
            </div>
          </div>
        </CCardBody>
      </CCard>

      {/*Modal for Accept/Reject/Counter Offers*/}
      <CModal
        visible={actionModal.open}
        onClose={resetModal}
        alignment="center"
      >
        <CCard className="m-0">
          <CCardBody>
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                {actionModal.type === "accept" && "Accept Offer"}
                {actionModal.type === "reject" && "Reject Offer"}
                {actionModal.type === "counter" && "Counter Offer"}
              </h5>

              <CButton color="light" className="p-0" onClick={resetModal}>
                <span aria-hidden="true">&times;</span>
              </CButton>
            </div>

            {/* Message */}
            <p className="text-muted mb-3">
              {actionModal.type === "accept" &&
                "Are you sure you want to accept this offer?"}

              {actionModal.type === "reject" &&
                "Please provide a reason for rejecting this offer."}

              {actionModal.type === "counter" &&
                "Propose a counter offer with price and discount."}
            </p>

            {/* Form */}
            <CForm>
              {actionModal.type === "counter" && (
                <>
                  {/* Max Buying Price (read-only) */}
                  <div className="mb-3">
                    <CFormLabel className="fw-semibold text-muted">
                      Max Buying Price
                    </CFormLabel>
                    <div className="fw-bold">
                      {formatCurrency(actionModal.offer?.max_buying_price)}
                    </div>
                  </div>

                  <CFormLabel className="fw-semibold mt-3">
                    Currency*
                  </CFormLabel>
                  <CFormSelect
                    value={counterCurrency}
                    onChange={(e) => setCounterCurrency(e.target.value)}
                    invalid={!!errors.counterCurrency}
                  >
                    <option value="">Select Currency</option>
                    <option value="AUD">AUD</option>
                    <option value="USD">USD</option>
                  </CFormSelect>
                  {errors.counterCurrency && (
                    <div className="text-danger small mt-1">
                      {errors.counterCurrency}
                    </div>
                  )}

                  <CFormLabel className="fw-semibold mt-3">
                    Counter Price*
                  </CFormLabel>
                  <CFormInput
                    type="number"
                    min={0}
                    value={counterPrice}
                    onChange={(e) => setCounterPrice(e.target.value)}
                    invalid={!!errors.counterPrice}
                    placeholder="Enter counter price"
                  />
                  {errors.counterPrice && (
                    <div className="text-danger small mt-1">
                      {errors.counterPrice}
                    </div>
                  )}

                  <CFormLabel className="fw-semibold mt-3">
                    Discount Percentage (%)*
                  </CFormLabel>
                  <CFormInput
                    type="number"
                    min={0}
                    max={100}
                    value={counterDiscount}
                    onChange={(e) => setCounterDiscount(e.target.value)}
                    invalid={!!errors.counterDiscount}
                    placeholder="Enter discount %"
                  />
                  {errors.counterDiscount && (
                    <div className="text-danger small mt-1">
                      {errors.counterDiscount}
                    </div>
                  )}
                </>
              )}

              <CFormLabel className="fw-semibold mt-3">
                Note {actionModal.type === "accept" ? "(Optional)" : "*"}
              </CFormLabel>
              <CFormTextarea
                component="textarea"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                invalid={!!errors.note}
                placeholder="Write your note here"
              />
              {errors.note && (
                <div className="text-danger small mt-1">{errors.note}</div>
              )}
            </CForm>

            {/* Footer buttons */}
            <div className="d-flex justify-content-end mt-4">
              <CButton color="secondary" className="me-2" onClick={resetModal}>
                Close
              </CButton>

              <CButton
                color={
                  actionModal.type === "accept"
                    ? "success"
                    : actionModal.type === "reject"
                      ? "danger"
                      : "warning"
                }
                onClick={handleSubmitAction}
              >
                Confirm
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CModal>

      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
      />
    </>
  );
};
export default ReceivedOffers;
